import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    Avatar,
    Badge,
    Box,
    IconButton,
    Typography,
} from '@mui/material';
import {
    CameraAlt,
    Check,
    Undo,
} from '@mui/icons-material';

import { HasPermissions } from '../../contexts/PermissionContext';
import ProjectContext from '../../contexts/ProjectContext';
import SettingsTextField from '../textfield/SettingsTextField';
import _ from 'lodash';


function GeneralSettings({ schema={}, settings={}, setSettings = () => {} }) {

    const projectContext = useContext(ProjectContext);
    const fileInputRef = useRef(null);
    const [generalSettings, setGeneralSettings] = useState(settings);
    const [hoverImage, setHoverImage] = useState(false);

    useEffect(() => {
        setGeneralSettings(settings)
        fileInputRef.current.value = null
    }, [settings])

    function handleChange(field, value) {
        const updatedSettings = _.cloneDeep(generalSettings);
        updatedSettings[field] = value;
        updateSettings(updatedSettings);
    }

    function handleDiscard() {
        updateSettings(settings);
        fileInputRef.current.value = null
    }

    function handleImageChange(event) {
        const file = event.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => {
                handleChange('Image', e.target.result); // Save as data URL
            };
            reader.readAsDataURL(file);
        }
    }

    function updateSettings(newSettings) {
        setGeneralSettings(newSettings);
        setSettings(newSettings);
    }

    return (
        <Box
            display="flex"
            border={'1px solid rgba(139, 142, 146, 1)'}
            borderRadius={4}
            position="relative"
        >
            {
                !_.isEqual(settings, generalSettings) && 
                <Box
                    position="absolute"
                    top={0}
                    right={0}
                    p={1}
                >
                    <IconButton
                        sx={{
                            color: 'rgba(180, 120, 120, 0.7)', // muted red
                            transition: 'color 0.2s',
                            '&:hover': {
                                color: 'rgb(211, 47, 47)', // vivid red
                            },
                        }}
                        onClick={handleDiscard}
                    >
                        <Undo
                            sx={{
                                fontSize: '16px',
                            }}
                        />
                    </IconButton>
                    <IconButton
                        sx={{
                            color: 'rgba(120, 180, 120, 0.7)', // muted green
                            fontSize: '16px',
                            transition: 'color 0.2s',
                            '&:hover': {
                                color: 'rgb(46, 225, 50)', // vivid green
                            },
                        }}
                    >
                        <Check
                            sx={{
                                fontSize: '16px',
                            }}
                        />
                    </IconButton>
                </Box>
            }
            <Box
                m={2}
                display="flex"
                flexDirection="column"
                gap={2}
            >
                <Typography
                    variant="h6"
                >
                    General Settings
                </Typography>

                <Box
                    display="flex"
                    alignItems="center"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        position="relative"
                    >
                        <Badge
                            overlap='circular'
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            badgeContent={
                                <Badge
                                    overlap='circular'
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    badgeContent={
                                        generalSettings?.Image !== settings?.Image &&
                                        <Box
                                            width={8}
                                            height={8}
                                            borderRadius={8}
                                            backgroundColor={'rgba(255, 193, 7, 1)'} // warning color (amber)
                                        />
                                    }
                                >
                                    <Avatar
                                    src={generalSettings?.Image}
                                    children={
                                        <img
                                            alt="Konsol"
                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            src='/logo/konsol-k-icon.svg'
                                        />
                                    }
                                        sx={{
                                            width: 35,
                                            height: 35,
                                            backgroundColor: 'rgba(0,0,0,0.8)',
                                            border: '2px solid rgba(139, 142, 146, 1)',
                                        }}
                                    />
                                </Badge>
                            }
                        >
                            <Avatar
                                onClick={HasPermissions(schema?.permissions?.write) ? () => fileInputRef.current.click() : () => {}}
                                onMouseEnter={() => setHoverImage(true)}
                                onMouseLeave={() => setHoverImage(false)}
                                src={generalSettings?.Image}
                                children={
                                    <img
                                        alt="Konsol"
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                        src='/logo/konsol-k-icon.svg'
                                    />
                                }
                                sx={{
                                    width: 110,
                                    height: 110,
                                    backgroundColor: hoverImage && HasPermissions(schema?.permissions?.write) ? 'rgba(255, 255, 255, 0.5)' : 'transparent',
                                    cursor: HasPermissions(schema?.permissions?.write) ? "pointer" : "default",
                                    border: hoverImage && HasPermissions(schema?.permissions?.write)
                                    ? '2px solid rgba(178, 235, 177, 1)'
                                    : '2px solid rgba(139, 142, 146, 1)',
                                }}
                            />
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleImageChange}
                            />
                            {hoverImage && HasPermissions(schema?.permissions?.write) &&
                                <Box
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    ml='2px'
                                    mt='2px'
                                    width={110}
                                    height={110}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    backgroundColor="rgba(0,0,0,0.4)"
                                    borderRadius="50%"
                                    sx={{ pointerEvents: 'none' }}
                                >
                                    <CameraAlt sx={{ color: 'rgba(178, 235, 177, 1)', fontSize: 30 }} />
                                </Box>
                            }
                        </Badge>
                    </Box>
                    <Box px={2}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                minHeight: 28,
                            }}
                        >
                            {generalSettings?.Production ?? ''}
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            color='rgb(150, 150, 150)'
                        >
                            {projectContext?.project_db ?? ''}
                        </Typography>
                    </Box>
                </Box>

                <SettingsTextField
                    description={schema?.data?.Production?.description ?? ''}
                    disabled={!HasPermissions(schema?.permissions?.write)}
                    label={schema?.data?.Production?.label ?? 'Production Name'}
                    onChange={(value) => handleChange('Production', value)}
                    original={settings?.Production ?? ''}
                    value={generalSettings?.Production ?? ''}
                />

                <SettingsTextField
                    description={schema?.data?.['Production Co.']?.description ?? ''}
                    disabled={!HasPermissions(schema?.permissions?.write)}
                    label={schema?.data?.['Production Co.']?.label ?? 'Production Company'}
                    onChange={(value) => handleChange('Production Co.', value)}
                    original={settings?.['Production Co.'] ?? ''}
                    value={generalSettings?.['Production Co.'] ?? ''}
                />

                <SettingsTextField
                    description={schema?.data?.Unit?.description ?? ''}
                    disabled={!HasPermissions(schema?.permissions?.write)}
                    label={schema?.data?.Unit?.label ?? 'Production Unit'}
                    onChange={(value) => handleChange('Unit', value)}
                    original={settings?.Unit ?? ''}
                    value={generalSettings?.Unit ?? ''}
                />

            </Box>
        </Box>
    );
}

export default GeneralSettings;